/**
 * ProgressPolicy — admin config for the Progress Gate (Round 17).
 *
 * Kept as its own tab, separate from Admin → Time (TimePolicy.jsx) — Matt's explicit call that
 * time and progress enforcement are not tied together. Three panels:
 *   1. Enforcement settings — on/off, auto-expiring pause/resume. Cadence is fixed (weekly, reset
 *      Monday America/Los_Angeles — see api/progress-gate.js) — not a configurable number.
 *   2. Bulk assign — "assign me to every unresolved project" in one click, using the SAME
 *      setClientManager + syncClientManagerToCore flow as the per-project picker below, just
 *      looped. Whoever is logged in when this is clicked is who gets assigned (ctx.currentMember).
 *   3. Client Manager roster — EVERY active Fixed-Fee-bearing project with its current manager
 *      (or "— unassigned —"), each with a picker to set/CHANGE it. setClientManager has no lock
 *      of its own (the onboarding wizard's "locked after Confirm Setup" only lives in
 *      OnboardingLauncher.jsx's React state, never enforced server-side), so this is a genuine,
 *      ongoing reassignment path — not a one-time backfill that goes stale once everyone has someone.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  getProgressGateSettings, setProgressGateSettings, pauseProgressGate, getClientManagerRoster,
} from '../../services/progressGate.js'
import { setClientManager, syncClientManagerToCore } from '../../services/onboarding.js'
import { getTeamMembers } from '../../services/members.js'
import { ctx } from '../../outline/ctx.js'

export default function ProgressPolicy({ dark }) {
  const [settings, setSettings] = useState(null)
  const [roster, setRoster] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  const [savingUid, setSavingUid] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'
  const surface  = dark ? '#0d1e24' : '#fff'

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [s, r, m] = await Promise.all([getProgressGateSettings(), getClientManagerRoster(), getTeamMembers()])
      setSettings(s || { enabled: false })
      setRoster(r || [])
      setMembers(m || [])
    } catch (e) { setMsg(e.message || 'Load failed') } finally { setLoading(false) }
  }, [])
  useEffect(() => { reload() }, [reload])

  function flash(t) { setMsg(t); setTimeout(() => setMsg(''), 4000) }
  function memberName(id) { const m = members.find(x => x.id === id); return m ? m.name : (id || '') }

  async function saveSettings(patch) {
    const next = { ...settings, ...patch }
    setSettings(next)
    const r = await setProgressGateSettings({ enabled: next.enabled === true })
    if (r.error) flash(r.error); else flash('Settings saved')
  }

  async function resumeNow() {
    const r = await setProgressGateSettings({ enabled: settings.enabled === true, paused_until: null })
    if (r.error) flash(r.error); else { flash('Enforcement resumed'); reload() }
  }

  // Shared by both the per-row picker and the bulk button — one project, one write, one Core sync.
  async function assignOne(projectUid, memberId) {
    const r = await setClientManager(projectUid, memberId)
    if (r && r.error) throw new Error(r.error)
    setRoster(list => list.map(p => (p.projectUid === projectUid ? { ...p, managerId: memberId } : p)))
    // Best-effort BQE Core sync — a failure here doesn't mean the assignment itself failed.
    return syncClientManagerToCore(projectUid, memberId).catch(e => ({ ok: false, error: e.message }))
  }

  async function assignManager(projectUid, memberId) {
    if (!memberId) return
    setSavingUid(projectUid)
    try {
      const sync = await assignOne(projectUid, memberId)
      if (sync.skipped) flash('Client Manager set (Core not updated: ' + sync.reason + ')')
      else if (sync.ok) flash('Client Manager set — Core updated (' + sync.results.length + ')')
      else if (sync.failedCount) flash('Client Manager set — Core update failed for ' + sync.failedCount + ' of ' + sync.results.length + ', check Core manually')
      else flash('Client Manager set (Core sync error: ' + (sync.error || 'unknown') + ')')
    } catch (e) { flash(e.message || 'Could not save') } finally { setSavingUid('') }
  }

  // "Assign me to every unresolved project" — same flow as the picker, looped. Whoever is logged
  // in when this is clicked is who gets assigned (ctx.currentMember reflects the live session).
  async function bulkAssignMe() {
    const me = ctx.currentMember
    if (!me || !me.id) { flash('Could not resolve your member record'); return }
    const targets = roster.filter(p => !p.managerId)
    if (!targets.length) { flash('Nothing unresolved to assign'); return }
    setBulkBusy(true)
    let okCount = 0, coreFailCount = 0
    try {
      for (const p of targets) {
        try {
          const sync = await assignOne(p.projectUid, me.id)
          okCount++
          if (sync && sync.failedCount) coreFailCount++
        } catch (e) { /* keep going — report the tally, don't abort the batch on one failure */ }
      }
      flash(`Assigned you to ${okCount} of ${targets.length} project${targets.length === 1 ? '' : 's'}` + (coreFailCount ? ` (${coreFailCount} had a Core sync issue — check Core manually)` : ''))
    } finally { setBulkBusy(false) }
  }

  const sel = { padding: '5px 8px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, borderRadius: 5, border: `0.5px solid ${border}`, background: surface, color: textPrim }
  const label = { fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: textMut, marginBottom: 8 }
  const th = { fontFamily: '"Montserrat",sans-serif', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: textMut, textAlign: 'left', padding: '6px 10px' }
  const td = { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: textPrim, padding: '6px 10px', borderTop: `0.5px solid ${border}` }

  if (loading) return <div style={{ padding: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: textMut }}>Loading…</div>

  const pausedActive = settings.paused_until && new Date(settings.paused_until) > new Date()
  const unresolvedCount = roster.filter(p => !p.managerId).length

  return (
    <div style={{ padding: '16px 20px' }}>
      {msg && <div style={{ marginBottom: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: dark ? '#7fd6a8' : '#1f9d6b' }}>{msg}</div>}

      <div style={label}>Enforcement settings</div>
      {pausedActive && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '8px 12px', borderRadius: 8, background: dark ? 'rgba(180,83,9,0.15)' : '#fdf3e4', border: '0.5px solid rgba(180,83,9,0.35)' }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: '#b45309' }}>
            Paused until {new Date(settings.paused_until).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} — enforcement resumes automatically.
          </span>
          <button style={{ ...sel, cursor: 'pointer', fontWeight: 600, padding: '4px 10px' }} onClick={resumeNow}>Resume now</button>
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-end', marginBottom: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: textPrim }}>
          <input type="checkbox" checked={!!settings.enabled} onChange={e => saveSettings({ enabled: e.target.checked })} />
          Gate enabled
        </label>
        <button style={{ ...sel, cursor: 'pointer' }} onClick={() => pauseProgressGate(24).then(reload)}>Pause for the rest of today</button>
      </div>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: textMut, marginBottom: 24, maxWidth: 640 }}>
        Blocks each project's <strong>Client Manager</strong> once an active Fixed-Fee phase's
        percent-complete hasn't been confirmed since this week's Monday. Resets every Monday
        (America/Los_Angeles) — if Monday is missed, the gate stays up until cleared, through the
        following Monday; it never stacks across multiple missed weeks. Hourly phases and phases
        with no resolved Client Manager are never gated.
      </div>

      <div style={label}>Bulk assign</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button style={{ ...sel, cursor: unresolvedCount && !bulkBusy ? 'pointer' : 'default', fontWeight: 600, opacity: unresolvedCount && !bulkBusy ? 1 : 0.5 }}
          onClick={bulkAssignMe} disabled={!unresolvedCount || bulkBusy}>
          {bulkBusy ? 'Assigning…' : `Assign me to all unresolved (${unresolvedCount})`}
        </button>
        <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: textMut, maxWidth: 460 }}>
          Sets whoever's currently logged in as Client Manager on every project below with none —
          same write + Core sync as the picker, just looped. Reassign any of them individually below,
          any time.
        </span>
      </div>

      <div style={label}>Client Manager roster</div>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: textMut, marginBottom: 8, maxWidth: 660 }}>
        Every active Fixed-Fee-bearing project. Changing the picker updates Airtable and pushes the
        matching update into BQE Core — this is the only place to fix a wrong assignment later, no
        separate "unlock" step needed.
      </div>
      <div style={{ overflowX: 'auto', border: `0.5px solid ${border}`, borderRadius: 8, marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Project</th><th style={th}>Current</th><th style={th}>Client Manager</th></tr></thead>
          <tbody>
            {roster.map(p => (
              <tr key={p.projectUid} style={{ opacity: savingUid === p.projectUid ? 0.6 : 1 }}>
                <td style={td}>{p.projectName || p.projectUid}</td>
                <td style={td}>{p.managerId ? memberName(p.managerId) : <span style={{ color: '#b45309' }}>— unassigned —</span>}</td>
                <td style={td}>
                  <select style={sel} value={p.managerId || ''} disabled={savingUid === p.projectUid} onChange={e => assignManager(p.projectUid, e.target.value)}>
                    <option value="">Select…</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {roster.length === 0 && (
              <tr><td style={td} colSpan={3}>No active Fixed-Fee-bearing projects found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
